// تقرير تطوير الميثك (للمراجعة فقط — لا يُنشر ولا يغيّر الحاسبة).
import React from "react";

const REG = [["gulf", "الخليجي"], ["medium", "المتوسط"], ["africa", "الأفريقي"]];
const n = (v) => (typeof v === "number" ? v.toLocaleString("en-US") : "—");
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("ar-SA", { timeZone: "Asia/Riyadh", dateStyle: "medium", timeStyle: "short" }) : "—");

const Table = ({ head, rows, testId }) => (
  <div className="overflow-x-auto rounded-xl border border-white/10">
    <table data-testid={testId} className="w-full text-[11px] sm:text-xs">
      <thead className="bg-white/[0.04] text-[#E5CB94]">
        <tr>{head.map((h) => <th key={h} className="px-2 py-2 text-right font-bold whitespace-nowrap">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-white/5 text-white/80">
            {r.map((c, j) => <td key={j} className="px-2 py-1.5 tabular-nums whitespace-nowrap">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const costCells = (o) => REG.map(([k]) => n(o?.[k]));

const Part = ({ title, children }) => (
  <div className="space-y-2">
    <h3 className="text-sm font-black text-white">{title}</h3>
    {children}
  </div>
);

export const MythicReport = ({ report }) => {
  if (!report) {
    return <div data-testid="mythic-report-empty" className="text-white/50 text-sm">لا يوجد تقرير بعد — يُجمع تلقائيًا مع كل فحص.</div>;
  }
  const d = report.data || {};
  const w = d.weapon || {};
  const c = d.char || {};
  const regHead = REG.map(([, l]) => `CP ${l}`);
  return (
    <div data-testid="mythic-report" className="space-y-4">
      <div className="text-[11px] text-white/60">
        أُنشئ: {fmt(report.created_at)} · آخر تحقق: {fmt(report.checked_at)} · الحالة: <span className="text-amber-200">بانتظار مراجعتك (غير منشور)</span>
      </div>
      <Part title="القاعدة العامة (ثابت)">
        <ul className="text-xs text-white/75 space-y-1 list-disc pr-5">
          <li>العملة: {d.currency}. الشراء يكون بحزم 10 سحبات، والحزمة تعطي {n(d.pack_rule?.size)} بطاقة/قطعة.</li>
          <li>عدد الحزم = تقريب للأعلى (المطلوب ÷ {n(d.pack_rule?.size)})، إلا إذا كان المطلوب أكثر من {n(d.pack_rule?.floor_above)} والباقي {n(d.pack_rule?.tolerance)} أو أقل فيُقرّب للأسفل.</li>
        </ul>
        <Table testId="mythic-pack-prices" head={["سعر الحزمة (10 سحبات)", ...regHead]} rows={[["سلاح مثك", ...costCells(d.pack_prices?.weapon)], ["شخصية مثك", ...costCells(d.pack_prices?.char)]]} />
      </Part>
      <Part title={`تطوير سلاح المثك — ${n(w.total_cards)} ${w.unit || ""}`}>
        <Table testId="mythic-weapon-levels" head={["المستوى", "البطاقات", "التراكمي", ...regHead.map((h) => `${h} (لوحده)`)]}
          rows={(w.levels || []).map((l) => [l.level, n(l.cards), n(l.cumulative), ...costCells(l.store_if_alone)])} />
        <Table testId="mythic-weapon-total" head={["التطوير الكامل", "الحزم", "السحبات", ...regHead]} rows={[
          ["من المتجر (ثابت)", n(w.full_store?.packs), n(w.full_store?.spins), ...costCells(w.full_store?.cp)],
          [`من العجلة مع بونص ${n(w.wheel_method?.bonus)} (تقديري)`, n(w.wheel_method?.packs), n(w.wheel_method?.spins), ...costCells(w.full_store?.wheel_cp)],
        ]} />
        <div className="text-[11px] text-white/50">محطات البونص: {(w.wheel_method?.milestones || []).join("، ")} سحبة</div>
      </Part>
      <Part title={`تطوير شخصية المثك — ${n(c.total_pieces)} ${c.unit || ""}`}>
        <Table testId="mythic-char-arms" head={["الفرع", "القطع لكل مرحلة", "المجموع", ...regHead.map((h) => `${h} (لوحده)`)]}
          rows={(c.arms || []).map((a) => [a.arm, (a.nodes || []).join(" / "), n(a.total), ...costCells(a.store_if_alone)])} />
        <Table testId="mythic-char-total" head={["التطوير الكامل", "الحزم", "السحبات", ...regHead]} rows={[
          ["من المتجر (ثابت)", n(c.full_store?.packs), n(c.full_store?.spins), ...costCells(c.full_store?.cp)],
          [`من العجلة مع بونص ${n(c.wheel_method?.bonus)} (تقديري)`, n(c.wheel_method?.packs), n(c.wheel_method?.spins), ...costCells(c.full_store?.wheel_cp)],
        ]} />
        <div className="text-[11px] text-white/50">محطات البونص: {(c.wheel_method?.milestones || []).join("، ")} سحبة</div>
      </Part>
      <Part title="القيم الجاهزة المنشورة في المصدر (مقرّبة)">
        <Table testId="mythic-reference" head={["القيمة", ...regHead]} rows={[
          ["تطوير السلاح الكامل", ...REG.map(([k]) => n(d.source_reference_totals?.[k]?.myth_weapon_dev))],
          ["فرع الفنش للشخصية", ...REG.map(([k]) => n(d.source_reference_totals?.[k]?.myth_char_finish))],
          ["تطوير الشخصية الكامل", ...REG.map(([k]) => n(d.source_reference_totals?.[k]?.myth_char_dev))],
        ]} />
      </Part>
      <Part title="ملاحظات عدم اليقين">
        <ul data-testid="mythic-uncertainty" className="text-xs text-amber-100/80 space-y-1 list-disc pr-5">{(d.uncertainty || []).map((t, i) => <li key={i}>{t}</li>)}</ul>
      </Part>
      {(d.guide_notes || []).length > 0 && (
        <Part title="نصوص الدليل العامة">
          <ul className="text-xs text-white/65 space-y-1 list-disc pr-5">{d.guide_notes.map((t, i) => <li key={i}>{t}</li>)}</ul>
        </Part>
      )}
      <Part title="مصدر كل معلومة">
        <ul className="text-xs text-white/60 space-y-1 list-disc pr-5">{Object.entries(d.provenance || {}).map(([k, v]) => <li key={k}>{v}</li>)}</ul>
      </Part>
    </div>
  );
};

export default MythicReport;
